import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, NavigationArrow, ChatCircleText, PaperPlaneTilt, MapPin, Truck, ShieldCheck, Wrench, CheckCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { BUSINESS } from "@/lib/business";

const HERO = "https://images.unsplash.com/photo-1761133381018-aed5063d22fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxzZW1pJTIwdHJ1Y2slMjBoaWdod2F5fGVufDB8fHx8MTc4NjA2MTM4NXww&ixlib=rb-4.1.0&q=85";
const BLANK = { name: "", phone: "", email: "", part_needed: "", vehicle_info: "", message: "" };
const HERO_ANIM = { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } };

const VALUE_PROPS = [
  { icon: Truck, t: "Huge Selection", d: "Parts for every truck & trailer" },
  { icon: Wrench, t: "New, Used & Rebuilt", d: "Options to fit any budget" },
  { icon: ShieldCheck, t: "Friendly Service", d: "Real people, honest advice" },
];

const ACTIONS = [
  { icon: Phone, title: "Call the Business", desc: BUSINESS.phoneDisplay, href: `tel:${BUSINESS.phoneTel}`, testid: "action-call" },
  { icon: NavigationArrow, title: "Visit the Store", desc: "Get directions to us", href: BUSINESS.mapsDirections, external: true, testid: "action-visit" },
  { icon: ChatCircleText, title: "Request a Quote", desc: "Tell us what you need", href: "#quote", testid: "action-quote" },
  { icon: PaperPlaneTilt, title: "Send a Message", desc: "Use our contact form", href: "#quote", testid: "action-contact" },
];

const HeroSection = () => (
  <section className="relative bg-black text-white overflow-hidden">
    <img src={HERO} alt="Truck on highway" className="absolute inset-0 w-full h-full object-cover opacity-40" />
    <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32">
      <motion.div {...HERO_ANIM} className="max-w-2xl">
        <span className="inline-block bg-[#3DA935] text-white text-xs font-bold uppercase tracking-[0.2em] px-3 py-1.5 mb-6">Truck Parts &amp; Tires</span>
        <h1 className="font-heading text-5xl md:text-7xl leading-[0.95] mb-5">QUALITY TRUCK PARTS<br />&amp; TIRES IN MILTON</h1>
        <p className="text-zinc-200 text-lg md:text-xl max-w-xl mb-8">Looking for truck parts or tires? Call us or request a quote today.</p>
        <div className="flex flex-wrap gap-3">
          <a href={`tel:${BUSINESS.phoneTel}`} data-testid="hero-call-button" className="inline-flex items-center gap-2 bg-[#3DA935] text-white font-bold uppercase tracking-widest text-sm px-7 py-4 hover:bg-white hover:text-black transition-colors">
            <Phone size={18} weight="fill" /> Call Us
          </a>
          <a href="#quote" data-testid="hero-quote-button" className="inline-flex items-center gap-2 border border-white text-white font-bold uppercase tracking-widest text-sm px-7 py-4 hover:bg-white hover:text-black transition-colors">
            <ChatCircleText size={18} weight="fill" /> Request a Quote
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);

const ValueProps = () => (
  <section className="border-b border-zinc-200 bg-zinc-50">
    <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-zinc-200">
      {VALUE_PROPS.map(({ icon: Icon, t, d }) => (
        <div key={t} className="flex items-center gap-4 py-6 md:px-8">
          <Icon size={32} className="text-[#3DA935]" />
          <div><div className="font-heading text-xl">{t}</div><div className="text-sm text-zinc-500">{d}</div></div>
        </div>
      ))}
    </div>
  </section>
);

const ActionsSection = () => (
  <section className="max-w-7xl mx-auto px-5 md:px-8 py-16">
    <div className="text-center mb-10">
      <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#3DA935]">Get In Touch</span>
      <h2 className="font-heading text-4xl md:text-5xl mt-1">How Can We Help?</h2>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="actions-grid">
      {ACTIONS.map(({ icon: Icon, title, desc, href, external, testid }) => (
        <a key={title} href={href} data-testid={testid}
          {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          className="group bg-zinc-50 border border-zinc-200 p-6 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-200 flex flex-col items-start">
          <div className="w-12 h-12 bg-[#3DA935] flex items-center justify-center mb-4 group-hover:bg-black transition-colors">
            <Icon size={24} weight="fill" color="#fff" />
          </div>
          <div className="font-heading text-2xl">{title}</div>
          <div className="text-sm text-zinc-500 mt-1">{desc}</div>
        </a>
      ))}
    </div>
  </section>
);

const Field = ({ label, testid, type = "text", value, onChange }) => (
  <div>
    <label className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-500 block mb-1.5">{label}</label>
    <input data-testid={testid} type={type} value={value} onChange={onChange}
      className="w-full px-3 py-2.5 border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#3DA935] text-sm" />
  </div>
);

const ContactInfo = () => (
  <div>
    <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#3DA935]">Contact Us</span>
    <h2 className="font-heading text-4xl md:text-5xl mt-1 mb-6">Request a Quote</h2>
    <p className="text-zinc-300 mb-8 max-w-md">Tell us the part or tire you need and a bit about your truck. We'll get back to you fast with pricing and availability.</p>

    <div className="space-y-5">
      <a href={`tel:${BUSINESS.phoneTel}`} data-testid="contact-phone-link" className="flex items-center gap-4 group">
        <div className="w-11 h-11 bg-[#3DA935] flex items-center justify-center shrink-0"><Phone size={20} weight="fill" color="#fff" /></div>
        <div><div className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Phone</div><div className="font-semibold group-hover:text-[#3DA935] transition-colors">{BUSINESS.phoneDisplay}</div></div>
      </a>
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 bg-[#3DA935] flex items-center justify-center shrink-0"><MapPin size={20} weight="fill" color="#fff" /></div>
        <div><div className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Address</div><div className="font-semibold">{BUSINESS.address}</div></div>
      </div>
      <a href={BUSINESS.mapsDirections} target="_blank" rel="noopener noreferrer" data-testid="get-directions-button"
        className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-sm px-6 py-3.5 hover:bg-[#3DA935] hover:text-white transition-colors">
        <NavigationArrow size={18} weight="fill" /> Get Directions
      </a>
    </div>
  </div>
);

const Confirmation = ({ onReset }) => (
  <div className="text-center py-14" data-testid="form-confirmation">
    <CheckCircle size={64} className="mx-auto text-[#3DA935] mb-4" weight="fill" />
    <h3 className="font-heading text-3xl mb-2">Thank You!</h3>
    <p className="text-zinc-600 mb-6">Your request has been sent. We'll get back to you as soon as possible.</p>
    <button onClick={onReset} data-testid="send-another-button" className="border border-black font-bold uppercase tracking-widest text-sm px-6 py-3 hover:bg-black hover:text-white transition-colors">Send Another</button>
  </div>
);

const QuoteForm = ({ form, set, submit, loading }) => (
  <form onSubmit={submit} className="space-y-4">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Full Name *" testid="quote-name" value={form.name} onChange={set("name")} />
      <Field label="Phone Number *" testid="quote-phone" value={form.phone} onChange={set("phone")} />
    </div>
    <Field label="Email Address" testid="quote-email" type="email" value={form.email} onChange={set("email")} />
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Part or Tire Needed" testid="quote-part" value={form.part_needed} onChange={set("part_needed")} />
      <Field label="Vehicle / Truck Info" testid="quote-vehicle" value={form.vehicle_info} onChange={set("vehicle_info")} />
    </div>
    <div>
      <label className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-500 block mb-1.5">Message *</label>
      <textarea data-testid="quote-message" rows={4} value={form.message} onChange={set("message")}
        className="w-full px-3 py-2.5 border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#3DA935] text-sm"
        placeholder="Tell us what you're looking for…" />
    </div>
    <button type="submit" disabled={loading} data-testid="submit-quote-button"
      className="w-full bg-[#3DA935] text-white font-bold uppercase tracking-widest text-sm py-4 hover:bg-black transition-colors disabled:opacity-50">
      {loading ? "Sending…" : "Send Request"}
    </button>
  </form>
);

const ContactSection = () => {
  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, phone and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/inquiries", form);
      setSubmitted(true);
      setForm(BLANK);
      toast.success("Request sent!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <section id="quote" className="bg-[#0a0a0a] text-white scroll-mt-24">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <ContactInfo />
        <div className="bg-white text-black p-6 md:p-8" data-testid="quote-form-card">
          {submitted
            ? <Confirmation onReset={() => setSubmitted(false)} />
            : <QuoteForm form={form} set={set} submit={submit} loading={loading} />}
        </div>
      </div>
    </section>
  );
};

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <HeroSection />
      <ValueProps />
      <ActionsSection />
      <ContactSection />
      <Footer />
    </div>
  );
}
